import { useEffect, useRef, useState } from 'react';

interface Options {
  /**
   * Activate all the strict validations.
   * Check that all the content is being displayed in the viewport (from top-left to bottom-right).
   * @default false
   */
  strict?: boolean;
}

const STRICT_THRESHOLD = 1.0;
const FALLBACK_THRESHOLD = 0.1;

/** Use the [Intersection Object API](https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API) to check the visibility of an element on the viewport. */
export function useIntersectionWatcher<E extends HTMLElement>(options?: Options) {
  const [visible, setVisible] = useState(false);
  const [haveBeenShown, setHaveBeenShown] = useState(false);

  const ref = useRef<E>(null);

  const isStrict = options?.strict ?? false;

  const check = (entries: IntersectionObserverEntry[]) => {
    const [first] = entries;

    // Allow to only check once have been shown.
    setHaveBeenShown(prev => prev || first.isIntersecting);
    // Check if the element is visible.
    setVisible(first.isIntersecting);
  };

  const observer = useRef(
    new IntersectionObserver(check, {
      threshold: isStrict ? STRICT_THRESHOLD : FALLBACK_THRESHOLD,
    }),
  );

  useEffect(() => {
    if (!ref.current) {
      return;
    }

    observer.current.observe(ref.current);
    // Force the observer to check the current element when the component mounts.
    // https://github.com/jebelapp/jebel/issues/1810
    observer.current.takeRecords();

    return () => {
      observer.current.disconnect();
    };
  }, [ref.current]);

  return {
    ref,
    visible,
    haveBeenShown,
  };
}
